import React, { memo, useEffect, useMemo, useState, useRef, forwardRef, useImperativeHandle, useCallback } from "react";
import * as XLSX from "xlsx";
import "../SimpleExcelViewer.css";

import {
  MIN_VISIBLE_ROWS, MIN_VISIBLE_COLS,
  DEFAULT_COL_WIDTH, DEFAULT_ROW_HEIGHT,
  MIN_COL_WIDTH, MIN_ROW_HEIGHT,
} from "./constants";
import { toColumnLabel } from "./utils/columnUtils";
import { textToHtml } from "./utils/htmlUtils";
import { buildSheetModel } from "./parsers/sheetModelBuilder";
import { buildWorkbookModelsWithExcelJS } from "./parsers/excelJSParser";
import FilterMenu from "./components/FilterMenu";
import ExcelImage from "./components/ExcelImage";
import ExcelChart from "./components/ExcelChart";

const SimpleExcelViewer = forwardRef(({
  // Page-aware identifiers used to resolve the Excel binary for this page via
  // the unified /api/v1/images/... endpoint (no separate OnlyOffice config call).
  documentId,
  pageNumber,
  openId,
  userId,
  // Back-compat: callers that still pass fileId are tolerated.
  fileId,
  height = "calc(87vh - 62px)",
  zoom = 1,
  // Accept raw float zoom scale (for Fit Page / Full Width support)
  zoomScale: zoomScaleProp,
  // Called when the Excel file is locked/encrypted and cannot be displayed.
  // The parent should fall back to the existing PDF rendering flow.
  onLocked,
}, ref) => {
  // Fall back to fileId when the caller hasn't been migrated to documentId yet.
  const effectiveDocumentId = documentId || fileId;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sheetNames, setSheetNames] = useState([]);
  const [activeSheet, setActiveSheet] = useState("");
  const [sheetDataByName, setSheetDataByName] = useState({});
  const [selectedCell, setSelectedCell] = useState({ row: 0, col: 0 });
  const [selection, setSelection] = useState({
    anchor: { row: 0, col: 0 },
    focus: { row: 0, col: 0 },
    dragging: false,
  });
  const [resizeState, setResizeState] = useState(null);
  const gridWrapperRef = useRef(null);
  const scrollBySheetRef = useRef({});
  // Holds the raw Excel binary so printFromSource() can re-parse it for printing
  const workbookBufferRef = useRef(null);
  // Holds the raw workbook object (SheetJS) available for printing purposes
  const rawWorkbookRef = useRef(null);

  const [filters, setFilters] = useState({});
  const [activeFilterCol, setActiveFilterCol] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadWorkbook = async () => {
      try {
        setLoading(true);
        setError("");

        // The unified /api/v1/images/{id}/{index}/{openId}/false/true endpoint
        // returns the Excel binary inline as base64 for Excel pages:
        //   { sucess: "true", isExcel: true, fileName, fileType, excelData: "<base64>" }
        // No separate download round-trip is needed.
        const apiBase = process.env.REACT_APP_API || "";
        // pageNumber is 1-based; the backend route uses a 0-based index.
        const pageIndex = Math.max(0, (Number(pageNumber) || 1) - 1);
        const requestUrl = `${apiBase}/images/${encodeURIComponent(effectiveDocumentId)}/${pageIndex}/${encodeURIComponent(openId || "")}/false/true`;

        const resp = await fetch(requestUrl, {
          method: "GET",
          credentials: "include",
        });
        if (!resp.ok) {
          throw new Error("Unable to load Excel file details.");
        }
        const respJson = await resp.json();
        const payload = respJson?.data || respJson;
        const base64 = payload?.excelData;
        if (!base64) {
          // The server did not return Excel data for this page — it is likely
          // a non-Excel page (e.g. PDF) that was requested while the viewer
          // was still mounted due to a page-navigation race.  Notify the
          // parent so it can fall back to the normal rendering flow instead
          // of displaying an error.
          if (!cancelled && typeof onLocked === "function") {
            onLocked();
          }
          return;
        }

        // Decode base64 → ArrayBuffer for XLSX / ExcelJS consumption.
        const binary = atob(base64);
        const len = binary.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
        const workbookBuffer = bytes.buffer;
        // Store the raw bytes so printFromSource() can use them later
        workbookBufferRef.current = workbookBuffer;

        let names = [];
        let dataMap = {};
        const fileType = String(payload?.fileType || "").toLowerCase();
        const lowerName = String(payload?.fileName || "").toLowerCase();

        // Always try ExcelJS unless we explicitly know it's a legacy .xls format
        const isLegacyXls = fileType === "xls" || (lowerName.includes(".xls") && !lowerName.includes(".xlsx"));

        if (!isLegacyXls) {
          try {
            const result = await buildWorkbookModelsWithExcelJS(workbookBuffer);
            names = result.sheetNames;
            dataMap = result.dataMap;
          } catch (e) {
            console.error("[SimpleExcelViewer] ExcelJS parsing failed, falling back to basic reader:", e);
            // Detect locked/encrypted workbooks — ExcelJS throws when the
            // ZIP structure is encrypted or the file is password-protected.
            const errMsg = String(e?.message || e || "").toLowerCase();
            if (
              errMsg.includes("encrypt") ||
              errMsg.includes("password") ||
              errMsg.includes("corrupted") ||
              errMsg.includes("end of central directory")
            ) {
              if (!cancelled && typeof onLocked === "function") {
                onLocked();
                return; // stop — parent will switch to PDF flow
              }
            }
            // fall back below
          }
        }

        if (names.length === 0) {
          let workbook;
          try {
            workbook = XLSX.read(workbookBuffer, {
              type: "array",
              cellFormula: true,
              cellStyles: true,
            });
          } catch (xlsxErr) {
            const msg = String(xlsxErr?.message || xlsxErr || "").toLowerCase();
            if (
              msg.includes("encrypt") ||
              msg.includes("password") ||
              msg.includes("cfb") ||
              msg.includes("corrupted")
            ) {
              if (!cancelled && typeof onLocked === "function") {
                onLocked();
                return; // parent switches to PDF flow
              }
            }
            throw xlsxErr;
          }
          // Keep a reference to the raw XLSX workbook for print use
          rawWorkbookRef.current = workbook;
          names = workbook.SheetNames || [];
          names.forEach((name) => {
            const ws = workbook.Sheets[name];
            dataMap[name] = buildSheetModel(ws || {});
          });
        } else {
          // Also parse a plain SheetJS workbook for print use (ExcelJS took the primary path)
          try {
            const wb = XLSX.read(workbookBuffer, { type: "array", cellFormula: true, cellStyles: true });
            rawWorkbookRef.current = wb;
          } catch (_) { /* non-fatal */ }
        }

        if (!cancelled) {
          setSheetNames(names);
          setSheetDataByName(dataMap);
          setActiveSheet((prev) => (prev && names.includes(prev) ? prev : names[0] || ""));
          setSelectedCell({ row: 0, col: 0 });
          setSelection({ anchor: { row: 0, col: 0 }, focus: { row: 0, col: 0 }, dragging: false });
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load Excel.");
          setLoading(false);
        }
      }
    };

    if (effectiveDocumentId) {
      loadWorkbook();
    } else {
      setLoading(false);
      setError("File details are missing.");
    }

    return () => {
      cancelled = true;
    };
    // Re-load whenever the document, current page, or user identity changes —
    // this is what makes multi-Excel page navigation work.
  }, [effectiveDocumentId, pageNumber, openId, userId]);

  // Imperative handle to trigger local printing for this component
  const componentRef = useRef(null);
  useImperativeHandle(ref, () => ({
    /**
     * printFromSource — preferred print path.
     * Converts every sheet in the original Excel binary (stored in workbookBufferRef)
     * to a clean HTML table via XLSX.utils.sheet_to_html(), assembles all sheets
     * into a single hidden iframe document, and calls window.print() on it.
     * This produces a faithful reproduction of the actual file content rather than
     * a screenshot of the current viewer DOM.
     */
    printFromSource: () => {
      const buf = workbookBufferRef.current;
      if (!buf) {
        console.warn("[SimpleExcelViewer] printFromSource: raw workbook buffer not available.");
        return;
      }

      let wb;
      try {
        // Re-parse from buffer so we get style data for sheet_to_html
        wb = XLSX.read(buf, { type: "array", cellFormula: true, cellStyles: true });
      } catch (err) {
        console.error("[SimpleExcelViewer] printFromSource: failed to parse buffer", err);
        return;
      }

      const sheetSections = wb.SheetNames.map((name, idx) => {
        const ws = wb.Sheets[name];
        // sheet_to_html produces a full <table> from the raw cell data
        const tableHtml = XLSX.utils.sheet_to_html(ws, { id: `sheet-${idx}`, editable: false });
        const pageBreak = idx > 0 ? `<div style="page-break-before: always;"></div>` : "";
        return `
          ${pageBreak}
          <h3 style="font-family: Calibri, Arial, sans-serif; font-size: 13px;
                     margin: 0 0 4px 0; color: #444;">${name}</h3>
          ${tableHtml}`;
      }).join("");

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.left = "-10000px";
      iframe.style.top = "-10000px";
      iframe.style.width = "1200px";
      iframe.style.height = "900px";
      iframe.style.border = "none";
      iframe.style.visibility = "hidden";
      document.body.appendChild(iframe);

      const targetDoc = iframe.contentWindow.document;
      targetDoc.open();
      targetDoc.write(`<!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8" />
            <title>Print Excel</title>
            <style>
              body {
                font-family: Calibri, "Segoe UI", Arial, sans-serif;
                font-size: 11pt;
                margin: 0;
                padding: 10px;
                color: #000;
              }
              table {
                border-collapse: collapse;
                width: max-content;
                margin-bottom: 4px;
              }
              td, th {
                border: 1px solid #bbb;
                padding: 2px 5px;
                font-size: 10pt;
                white-space: pre-wrap;
                vertical-align: middle;
                min-width: 60px;
              }
              @media print {
                @page { margin: 1cm; size: landscape; }
                * {
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                h3 { margin-top: 0; }
              }
            </style>
          </head>
          <body>${sheetSections}</body>
        </html>`);
      targetDoc.close();

      const removeIframe = () => {
        try { document.body.removeChild(iframe); } catch (_) { /* already removed */ }
      };

      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (err) {
          console.error("[SimpleExcelViewer] printFromSource print() failed:", err);
        }
        if (iframe.contentWindow) {
          iframe.contentWindow.addEventListener("afterprint", removeIframe, { once: true });
        }
        setTimeout(removeIframe, 60000);
      }, 400);
    },

    /**
     * printAsPdf — legacy DOM-clone print path (kept as fallback).
     */
    printAsPdf: () => {
      if (!componentRef.current) return;
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.left = "-10000px";
      iframe.style.top = "-10000px";
      iframe.style.width = "1200px";
      iframe.style.height = "800px";
      iframe.style.border = "none";
      iframe.style.visibility = "hidden";
      document.body.appendChild(iframe);

      const tableClone = componentRef.current.cloneNode(true);
      tableClone.style.zoom = "";
      const tableHTML = tableClone.outerHTML;

      const targetDoc = iframe.contentWindow.document;
      targetDoc.open();
      targetDoc.write(`
        <html>
          <head>
            <title>Print Excel</title>
            <style>
              body { font-family: Calibri, "Segoe UI", Arial, sans-serif; padding: 10px; margin: 0; }
              .simple-excel-grid { border-collapse: collapse; width: max-content; table-layout: auto; }
              .simple-excel-grid th, .simple-excel-grid td {
                border: 1px solid #d0d0d0; padding: 2px 4px; font-size: 12px;
                line-height: 1.4; vertical-align: middle; white-space: nowrap;
              }
              .simple-excel-grid thead th { background: #f3f3f3; font-weight: bold; text-align: center; }
              .simple-excel-grid tbody th { background: #f3f3f3; font-weight: bold; text-align: center; min-width: 44px; width: 44px; }
              .simple-excel-grid td { background: #ffffff; }
              .simple-excel-cell {
                width: 100%; display: block; overflow: hidden;
                text-overflow: ellipsis; white-space: inherit; word-break: normal;
              }
              .filter-excel-btn, .col-resize-handle, .row-resize-handle { display: none !important; }
              @media print {
                @page { margin: 0.5cm; }
                * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              }
            </style>
          </head>
          <body>${tableHTML}</body>
        </html>
      `);
      targetDoc.close();

      const removeIframe = () => {
        try { document.body.removeChild(iframe); } catch (_) { /* already removed */ }
      };

      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (err) {
          console.error("[SimpleExcelViewer] Print failed:", err);
        }
        if (iframe.contentWindow) {
          iframe.contentWindow.addEventListener("afterprint", removeIframe, { once: true });
        }
        setTimeout(removeIframe, 60000);
      }, 600);
    }
  }));

  const activeModel = useMemo(() => {
    if (!activeSheet) return null;
    return sheetDataByName[activeSheet] || null;
  }, [activeSheet, sheetDataByName]);

  const normalizedRows = useMemo(() => activeModel?.rows || [], [activeModel?.rows]);
  const maxCols = activeModel?.maxCols || MIN_VISIBLE_COLS;
  const colWidths = activeModel?.colWidths || new Array(maxCols).fill(DEFAULT_COL_WIDTH);
  const rowHeights = activeModel?.rowHeights || new Array(normalizedRows.length).fill(DEFAULT_ROW_HEIGHT);
  const cellStyles = activeModel?.styles || {};
  const cellRichHtml = activeModel?.richHtml || {};
  const sheetImages = useMemo(() => activeModel?.images || [], [activeModel?.images]);
  const sheetCharts = useMemo(() => activeModel?.charts || [], [activeModel?.charts]);
  const cellMerges = activeModel?.merges || {};
  const cellLinks = activeModel?.links || {};
  const cellDataValidations = activeModel?.dataValidations || {};

  // Dropdowns are read-only — we only display the selected value, no interaction.

  // Debug: Log visual content data
  useEffect(() => {
    console.log('[SimpleExcelViewer] Sheet data updated:', {
      sheetName: activeSheet,
      imagesCount: sheetImages.length,
      chartsCount: sheetCharts.length,
      images: sheetImages,
      charts: sheetCharts,
      colWidths: colWidths.slice(0, 10),
      rowHeights: rowHeights.slice(0, 10)
    });
  }, [activeSheet, sheetImages, sheetCharts]);

  const normalizedSelection = useMemo(() => {
    const r1 = Math.min(selection.anchor.row, selection.focus.row);
    const r2 = Math.max(selection.anchor.row, selection.focus.row);
    const c1 = Math.min(selection.anchor.col, selection.focus.col);
    const c2 = Math.max(selection.anchor.col, selection.focus.col);
    return { r1, r2, c1, c2 };
  }, [selection.anchor.col, selection.anchor.row, selection.focus.col, selection.focus.row]);

  const selectionSummary = useMemo(() => {
    const rowsCount = normalizedSelection.r2 - normalizedSelection.r1 + 1;
    const colsCount = normalizedSelection.c2 - normalizedSelection.c1 + 1;
    return rowsCount === 1 && colsCount === 1 ? "" : `${rowsCount}R x ${colsCount}C`;
  }, [normalizedSelection.c1, normalizedSelection.c2, normalizedSelection.r1, normalizedSelection.r2]);

  const selectedCellLabel = useMemo(
    () => `${toColumnLabel(selectedCell.col)}${selectedCell.row + 1}`,
    [selectedCell.col, selectedCell.row]
  );

  const selectedCellText = useMemo(() => {
    if (!activeModel) return "";
    const formula = activeModel.formulas?.[`${selectedCell.row}:${selectedCell.col}`];
    if (formula) return formula;
    const value = activeModel.rows?.[selectedCell.row]?.[selectedCell.col];
    return value == null ? "" : String(value);
  }, [activeModel, selectedCell.col, selectedCell.row]);

  /**
   * Parses a cell reference like "A1", "B10", "AA3" into { row, col } (0-based).
   * Returns null if the ref doesn't match the expected pattern.
   */
  const parseCellRef = useCallback((ref) => {
    if (!ref) return null;
    const m = ref.match(/^([A-Za-z]+)(\d+)$/);
    if (!m) return null;
    const letters = m[1].toUpperCase();
    let col = 0;
    for (let i = 0; i < letters.length; i++) col = col * 26 + (letters.charCodeAt(i) - 64);
    return { row: parseInt(m[2]) - 1, col: col - 1 };
  }, []);

  /**
   * Handles clicks on hyperlink cells.
   * - Internal sheet links (Excel format: "#SheetName!A1" or "SheetName!A1"):
   *   switches to the target sheet and scrolls/selects the target cell.
   * - Same-sheet cell links (e.g. "#A1" with no "!"):
   *   scrolls to and selects the target cell on the current sheet.
   * - External URLs: opens in a new tab safely.
   */
  const handleLinkClick = useCallback((url) => {
    if (!url) return;

    // Strip leading "#" that Excel uses for internal refs
    const deFragmented = url.startsWith('#') ? url.slice(1) : url;

    // Check: no protocol (no "://") → it's an internal/intra-workbook ref
    if (!url.includes('://')) {
      if (deFragmented.includes('!')) {
        // Format: SheetName!CellRef or 'Sheet Name'!CellRef
        const [sheetPart, cellRefPart] = deFragmented.split('!');
        const targetSheet = sheetPart.replace(/^'|'$/g, '');

        if (sheetNames.includes(targetSheet)) {
          // Switch sheet
          if (targetSheet !== activeSheet) {
            setActiveSheet(targetSheet);
          }
          // Navigate to the target cell after sheet switch
          const cellPos = parseCellRef(cellRefPart);
          if (cellPos) {
            setSelectedCell(cellPos);
            setSelection({ anchor: cellPos, focus: cellPos, dragging: false });
            // Scroll to the cell after a short delay to let the sheet render
            setTimeout(() => {
              const el = gridWrapperRef.current;
              if (el) {
                const targetLeft = colWidths.slice(0, cellPos.col).reduce((sum, w) => sum + w, 0);
                const targetTop = rowHeights.slice(0, cellPos.row).reduce((sum, h) => sum + h, 0);
                el.scrollLeft = Math.max(0, targetLeft - 50);
                el.scrollTop = Math.max(0, targetTop - 50);
              }
            }, 100);
          }
          return;
        }
      } else {
        // Same-sheet cell reference (e.g. "#A1" → "A1")
        const cellPos = parseCellRef(deFragmented);
        if (cellPos) {
          setSelectedCell(cellPos);
          setSelection({ anchor: cellPos, focus: cellPos, dragging: false });
          const el = gridWrapperRef.current;
          if (el) {
            const targetLeft = colWidths.slice(0, cellPos.col).reduce((sum, w) => sum + w, 0);
            const targetTop = rowHeights.slice(0, cellPos.row).reduce((sum, h) => sum + h, 0);
            el.scrollLeft = Math.max(0, targetLeft - 50);
            el.scrollTop = Math.max(0, targetTop - 50);
          }
          return;
        }
      }
    }

    // External URL — open in new tab
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [sheetNames, activeSheet, parseCellRef, colWidths, rowHeights]);

  // Clear filters on sheet change
  useEffect(() => {
    setFilters({});
    setActiveFilterCol(null);
  }, [activeSheet]);

  const toggleFilterMenu = (colIndex, event) => {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    setActiveFilterCol(prev => prev === colIndex ? null : colIndex);
  };

  // Close filter menu clicking outside
  useEffect(() => {
    if (activeFilterCol === null) return undefined;
    const onClick = () => setActiveFilterCol(null);
    window.addEventListener("click", onClick);
    return () => window.removeEventListener("click", onClick);
  }, [activeFilterCol]);

  const applyFilter = (colIndex, selectedValues) => {
    setFilters(prev => {
      const next = { ...prev };
      if (!selectedValues) delete next[colIndex];
      else next[colIndex] = selectedValues;
      return next;
    });
  };

  const isRowVisible = useCallback((rowIndex) => {
    // Row 0 is always visible — treat it as the frozen header row
    if (rowIndex === 0) return true;
    if (Object.keys(filters).length === 0) return true;
    for (const colIndex in filters) {
      const allowed = filters[colIndex];
      if (allowed && allowed.size > 0) {
        const v = normalizedRows[rowIndex][colIndex];
        const strVal = v == null || v === "" ? "(Blank)" : String(v);
        if (!allowed.has(strVal)) return false;
      }
    }
    return true;
  }, [filters, normalizedRows]);

  useEffect(() => {
    if (!selection.dragging) return undefined;
    const onMouseUp = () => setSelection((prev) => ({ ...prev, dragging: false }));
    window.addEventListener("mouseup", onMouseUp);
    return () => window.removeEventListener("mouseup", onMouseUp);
  }, [selection.dragging]);

  useEffect(() => {
    if (!resizeState) return undefined;

    const onMouseMove = (event) => {
      const delta = resizeState.type === "col" ? event.clientX - resizeState.start : event.clientY - resizeState.start;
      const nextSize = Math.max(
        resizeState.type === "col" ? MIN_COL_WIDTH : MIN_ROW_HEIGHT,
        resizeState.initial + delta
      );

      setSheetDataByName((prev) => {
        const sheet = prev[activeSheet];
        if (!sheet) return prev;

        if (resizeState.type === "col") {
          const nextCols = [...sheet.colWidths];
          nextCols[resizeState.index] = nextSize;
          return { ...prev, [activeSheet]: { ...sheet, colWidths: nextCols } };
        }

        const nextRows = [...sheet.rowHeights];
        nextRows[resizeState.index] = nextSize;
        return { ...prev, [activeSheet]: { ...sheet, rowHeights: nextRows } };
      });
    };

    const onMouseUp = () => {
      setResizeState(null);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [activeSheet, resizeState]);

  // Keep scroll position independent per sheet.
  useEffect(() => {
    const el = gridWrapperRef.current;
    if (!el || !activeSheet) return undefined;

    const onScroll = () => {
      scrollBySheetRef.current[activeSheet] = { left: el.scrollLeft, top: el.scrollTop };
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [activeSheet]);

  useEffect(() => {
    const el = gridWrapperRef.current;
    if (!el || !activeSheet) return;
    const pos = scrollBySheetRef.current[activeSheet];
    let raf1;
    let raf2;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (!pos) {
          el.scrollLeft = 0;
          el.scrollTop = 0;
        } else {
          el.scrollLeft = pos.left || 0;
          el.scrollTop = pos.top || 0;
        }
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [activeSheet]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        !["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key) ||
        e.target.tagName.toLowerCase() === "input" ||
        e.target.tagName.toLowerCase() === "textarea" ||
        !activeModel
      ) {
        return;
      }

      e.preventDefault();

      setSelectedCell((prev) => {
        let newRow = prev.row;
        let newCol = prev.col;

        if (e.key === "ArrowUp") newRow = Math.max(0, prev.row - 1);
        if (e.key === "ArrowDown") newRow = Math.min(normalizedRows.length - 1, prev.row + 1);
        if (e.key === "ArrowLeft") newCol = Math.max(0, prev.col - 1);
        if (e.key === "ArrowRight") newCol = Math.min(maxCols - 1, prev.col + 1);

        const newCell = { row: newRow, col: newCol };

        if (e.shiftKey) {
          setSelection((prevSel) => ({ ...prevSel, focus: newCell }));
        } else {
          setSelection({ anchor: newCell, focus: newCell, dragging: false });
        }

        // Auto-scroll into view
        setTimeout(() => {
          const el = gridWrapperRef.current;
          if (!el) return;

          let targetLeft = 0;
          for (let i = 0; i < newCol; i++) targetLeft += colWidths[i] || DEFAULT_COL_WIDTH;
          
          let targetTop = 0;
          for (let i = 0; i < newRow; i++) targetTop += rowHeights[i] || DEFAULT_ROW_HEIGHT;
          
          const cellWidth = colWidths[newCol] || DEFAULT_COL_WIDTH;
          const cellHeight = rowHeights[newRow] || DEFAULT_ROW_HEIGHT;

          const scrollLeft = el.scrollLeft;
          const scrollTop = el.scrollTop;
          const clientWidth = el.clientWidth;
          const clientHeight = el.clientHeight;

          const cornerWidth = 44; // Row header approx width
          const cornerHeight = 24; // Col header approx height

          if (targetLeft + cellWidth + cornerWidth > scrollLeft + clientWidth) {
            el.scrollLeft = targetLeft + cellWidth + cornerWidth - clientWidth + 20;
          } else if (targetLeft < scrollLeft) {
            el.scrollLeft = Math.max(0, targetLeft - 20);
          }

          if (targetTop + cellHeight + cornerHeight > scrollTop + clientHeight) {
            el.scrollTop = targetTop + cellHeight + cornerHeight - clientHeight + 20;
          } else if (targetTop < scrollTop) {
            el.scrollTop = Math.max(0, targetTop - 20);
          }
        }, 10);

        return newCell;
      });
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeModel, normalizedRows.length, maxCols, colWidths, rowHeights]);

  const startResize = (type, index, event) => {
    event.preventDefault();
    event.stopPropagation();
    setResizeState({
      type,
      index,
      start: type === "col" ? event.clientX : event.clientY,
      initial: type === "col" ? colWidths[index] : rowHeights[index],
    });
  };

  const startSelection = (row, col, event) => {
    const isShift = event.shiftKey;
    const isToggle = event.ctrlKey || event.metaKey;

    if (isShift) {
      setSelection((prev) => ({
        anchor: prev.anchor,
        focus: { row, col },
        dragging: true,
      }));
      setSelectedCell({ row, col });
      return;
    }

    if (isToggle) {
      setSelection({
        anchor: { row, col },
        focus: { row, col },
        dragging: false,
      });
      setSelectedCell({ row, col });
      return;
    }

    setSelection({
      anchor: { row, col },
      focus: { row, col },
      dragging: true,
    });
    setSelectedCell({ row, col });
  };

  const extendSelection = (row, col) => {
    setSelection((prev) => (prev.dragging ? { ...prev, focus: { row, col } } : prev));
  };

  // Compute zoom scale unconditionally (before any early returns) — React Hook rules
  const zoomScale = (typeof zoomScaleProp === "number" && zoomScaleProp > 0)
    ? zoomScaleProp
    : (zoom === 1 ? 1 : zoom === 2 ? 1.25 : zoom === 3 ? 1.5 : zoom === 4 ? 1.75 : 2);

  // Memoized grid body
  const gridBody = useMemo(() => (
    <tbody>
      {normalizedRows.map((row, rowIndex) => {
        if (!isRowVisible(rowIndex)) return null;
        return (
          <tr key={`row-${rowIndex}`} style={{ height: `${rowHeights[rowIndex]}px` }}>
            <th style={{ height: `${rowHeights[rowIndex]}px` }}>
              {rowIndex + 1}
              <span
                className="row-resize-handle"
                onMouseDown={(event) => startResize("row", rowIndex, event)}
              />
            </th>
            {row.map((cell, colIndex) => {
              const key = `${rowIndex}:${colIndex}`;
              const mergeInfo = cellMerges[key];
              // Skip slave cells of a merged region
              if (mergeInfo === null) return null;
              const html = cellRichHtml[key] || textToHtml(cell ?? "");
              const mergeSpan = mergeInfo || {};
              const linkUrl = cellLinks[key];
              return (
                <td
                  key={`cell-${rowIndex}-${colIndex}`}
                  colSpan={mergeSpan.colSpan || 1}
                  rowSpan={mergeSpan.rowSpan || 1}
                  onMouseDown={(event) => {
                    // Don't start cell selection drag on link cells — let the click through
                    if (!linkUrl) startSelection(rowIndex, colIndex, event);
                  }}
                  onMouseEnter={() => extendSelection(rowIndex, colIndex)}
                  onClick={linkUrl ? (e) => { e.preventDefault(); handleLinkClick(linkUrl); } : undefined}
                  className={[
                    selectedCell.row === rowIndex && selectedCell.col === colIndex ? "selected-cell" : "",
                    rowIndex >= normalizedSelection.r1 &&
                      rowIndex <= normalizedSelection.r2 &&
                      colIndex >= normalizedSelection.c1 &&
                      colIndex <= normalizedSelection.c2
                      ? `in-selection ${rowIndex === normalizedSelection.r1 ? "sel-top" : ""} ${rowIndex === normalizedSelection.r2 ? "sel-bottom" : ""} ${colIndex === normalizedSelection.c1 ? "sel-left" : ""} ${colIndex === normalizedSelection.c2 ? "sel-right" : ""}`
                      : "",
                    linkUrl ? "has-link" : "",
                  ].join(" ").trim()}
                  style={{
                    width: `${colWidths[colIndex]}px`,
                    minWidth: `${colWidths[colIndex]}px`,
                    maxWidth: `${mergeSpan.colSpan > 1 ? "none" : `${colWidths[colIndex]}px`}`,
                    ...(cellStyles[key] || {}),
                    ...(linkUrl ? { cursor: "pointer" } : {}),
                  }}
                  title={linkUrl ? `${cell == null ? "" : String(cell)}\n${linkUrl}` : (cell == null ? "" : String(cell))}
                >
                  <div
                    className={linkUrl ? "simple-excel-cell simple-excel-link" : "simple-excel-cell"}
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                </td>
              );
            })}
          </tr>
        );
      })}
    </tbody>
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [
    normalizedRows, rowHeights, colWidths, cellStyles, cellRichHtml, cellMerges, cellLinks,
    isRowVisible, normalizedSelection, selectedCell,
    startResize, startSelection, extendSelection, handleLinkClick,
  ]);

  if (loading) {
    return <div className="simple-excel-viewer-status" style={{ height }}>Loading Excel...</div>;
  }

  if (error) {
    return <div className="simple-excel-viewer-status simple-excel-viewer-error" style={{ height }}>{error}</div>;
  }

  if (sheetNames.length === 0) {
    return <div className="simple-excel-viewer-status" style={{ height }}>No sheets found in this file.</div>;
  }

  return (
    <div className="simple-excel-viewer" style={{ height }}>
      <div className="simple-excel-formula-bar">
        <div className="simple-excel-name-box">{selectedCellLabel}</div>
        <div className="simple-excel-fx-btn" title="Formula">
          <em>f</em><sub>x</sub>
        </div>
        <div
          className={[
            "simple-excel-formula-input",
            selectionSummary
              ? "formula-bar-selection"
              : selectedCellText.startsWith("=")
              ? "formula-bar-formula"
              : "",
          ].join(" ").trim()}
          title={selectedCellText}
        >
          {selectionSummary ? `Selection: ${selectionSummary}` : selectedCellText}
        </div>
      </div>

      <div className="simple-excel-grid-wrapper" ref={gridWrapperRef}>
        <table className="simple-excel-grid" style={{ zoom: zoomScale }} ref={componentRef}>
          <thead>
            <tr>
              <th className="corner-cell" />
              {Array.from({ length: maxCols }).map((_, colIndex) => (
                <th
                  key={`col-${colIndex}`}
                  style={{ minWidth: `${colWidths[colIndex]}px`, width: `${colWidths[colIndex]}px` }}
                >
                  {toColumnLabel(colIndex)}
                  <span
                    className={`filter-excel-btn ${filters[colIndex] ? 'filtered' : ''}`}
                    onClick={(e) => toggleFilterMenu(colIndex, e)}
                    title="Filter"
                  >
                    {filters[colIndex] ? (
                      <span className="material-icons" style={{ fontSize: '13px' }}>filter_alt</span>
                    ) : (
                      <span className="material-icons" style={{ fontSize: '15px' }}>arrow_drop_down</span>
                    )}
                  </span>
                  {activeFilterCol === colIndex && (
                    <FilterMenu
                      colIndex={colIndex}
                      rows={normalizedRows}
                      currentFilter={filters[colIndex]}
                      onApply={(sel) => applyFilter(colIndex, sel)}
                      onClose={() => setActiveFilterCol(null)}
                    />
                  )}
                  <span
                    className="col-resize-handle"
                    onMouseDown={(event) => startResize("col", colIndex, event)}
                  />
                </th>
              ))}
            </tr>
          </thead>
          {gridBody}
        </table>

        {/* Visual Content Overlay — positioned absolutely within the grid
            wrapper.  The overlay is part of the scroll content so it moves
            with the table.  zoomScale is passed so images / charts can
            scale their coordinates to match the zoomed table.              */}
        {/* key={activeSheet} forces React to unmount/remount the overlay on
             sheet switch so stale chart/image positions don't bleed through */}
        <div key={activeSheet} className="excel-visual-content-overlay" style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', zoom: zoomScale }}>
          {sheetImages.map((image, index) => (
            <div key={`image-${index}`} style={{ pointerEvents: 'auto' }}>
              <ExcelImage
                image={image}
                colWidths={colWidths}
                rowHeights={rowHeights}
                zoomScale={1}
              />
            </div>
          ))}
          {sheetCharts.map((chart, index) => (
            <div key={`chart-${index}`} style={{ pointerEvents: 'auto' }}>
              <ExcelChart
                chart={chart}
                colWidths={colWidths}
                rowHeights={rowHeights}
                zoomScale={1}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="simple-excel-sheetbar">
        <div className="simple-excel-tabs" role="tablist" aria-label="Sheets">
          {sheetNames.map((name) => (
            <button
              key={name}
              type="button"
              className={`simple-excel-tab ${activeSheet === name ? "active" : ""}`}
              onClick={() => setActiveSheet(name)}
              title={name}
            >
              {name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
});

export default memo(SimpleExcelViewer);
